import {
  returnAllTradeDetailsFromGenericParams,
  returnOPLegFormThisTrade,
} from "@/lib/agGridColDefs/colDefGlobalHelpers";
import { ExecutionRequestStatus } from "@/lib/interfaces and Enums/ENUMExecutionRequestStatus";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { SecurityTypeCode } from "@/lib/interfaces and Enums/ENUMSecurityTypeCode";
import { StrategyType } from "@/lib/interfaces and Enums/ENUMStrategyType";
import { InstrumentTypeCode } from "@/lib/interfaces and Enums/ENUMInstrumentTypeCode";
import { StructureTypeCode } from "@/lib/interfaces and Enums/ENUMStructureTypeCode";
import { ExecutionStrategy } from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import tradeDataStore from "@/store/tradeDataStore";
import { defaultStatusCannotBeEdited } from "./columnHelpers/cellCanBeEdited";
import { findCPOnTradeByBlockId } from "./tradeLookupHelpers";
import { ColId } from "./ENUMActionSummaryColIds";
import { ColDef } from "ag-grid-community";

/**
 * Reorders columns for Orders by moving a column to appear after a target column.
 * Only use this for Order-specific column ordering - RFQs could call this, but if called by orders - NO CHANGE to RFQs.
 */
export function reorderColumnsForOrders(
  colDefs: ColDef[],
  reorderRules: { colId: ColId; afterColId: ColId }[],
): ColDef[] {
  const result = [...colDefs];

  for (const rule of reorderRules) {
    const colIndex = result.findIndex((c) => c.colId === rule.colId);
    const targetIndex = result.findIndex((c) => c.colId === rule.afterColId);

    if (colIndex !== -1 && targetIndex !== -1 && colIndex !== targetIndex) {
      const [col] = result.splice(colIndex, 1);
      // Calculate insert position: after target, adjusting for the splice
      const insertAt = colIndex < targetIndex ? targetIndex : targetIndex + 1;
      result.splice(insertAt, 0, col);
    }
  }
  return result;
}

// --- Flattened fills column wrapping helpers ---
// Used to wrap cell renderers/formatters so block-only columns return null on fill rows,
// and fill-only columns return null on block rows.

const blockOnlyColIds: (ColId | string)[] = [
  ColId.expand,
  ColId.direction,
  ColId.notionalLeg1,
  ColId.security,
  ColId.settlementDate,
  ColId.level,
  ColId.feeAdjustedQuote,
  ColId.indicativeMid,
  ColId.book,
  ColId.isin,
  ColId.isinOutright,
  ColId.referenceIndex,
  ColId.quotedMargin,
  ColId.currentCoupon,
];

/**
 * Wraps a cell renderer to only render on block rows (returns null for fill rows).
 * Renders via JSX so the inner renderer gets its own React fiber - calling it as a
 * plain function hoists its hooks onto the wrapper and corrupts hook order when
 * rowType changes between renders (e.g. fills removed on order rejection).
 */
function wrapBlockOnlyRenderer(renderer: any) {
  if (!renderer) return renderer;
  const BlockRenderer = renderer;
  return (params: any) => {
    if (params.data?.rowType === "fill") return null;
    return typeof BlockRenderer === "function" ? (
      <BlockRenderer {...params} />
    ) : null;
  };
}

/**
 * Wraps a valueFormatter to return empty string on fill rows.
 */
function wrapBlockOnlyFormatter(formatter: any) {
  if (!formatter) return formatter;
  return (params: any) => {
    if (params.data?.rowType === "fill") return "";
    return formatter(params);
  };
}

/**
 * Creates a dual renderer that delegates to blockRenderer for block rows
 * and fillRenderer for fill rows.
 */
export function wrapDualRenderer(blockRenderer: any, fillRenderer: any) {
  const BlockRenderer = blockRenderer;
  const FillRenderer = fillRenderer;
  // JSX (not plain function calls) so each renderer keeps its own React fiber,
  // otherwise a fill-to-block flip on one cell corrupts React's hook order
  return (params: any) => {
    if (params.data?.rowType === "fill") {
      return typeof FillRenderer === "function" ? (
        <FillRenderer {...params} />
      ) : null;
    }
    return typeof BlockRenderer === "function" ? (
      <BlockRenderer {...params} />
    ) : null;
  };
}

/**
 * Creates a dual valueFormatter that delegates based on rowType.
 */
export function wrapDualFormatter(blockFormatter: any, fillFormatter: any) {
  return (params: any) => {
    if (params.data?.rowType === "fill") {
      return typeof fillFormatter === "function" ? fillFormatter(params) : "";
    }
    return typeof blockFormatter === "function" ? blockFormatter(params) : "";
  };
}

/**
 * Wraps a cell renderer to only render on fill rows (returns null for block rows).
 */
export function wrapFillOnlyRenderer(renderer: any) {
  if (!renderer) return renderer;
  const FillRenderer = renderer;
  return (params: any) => {
    if (params.data?.rowType !== "fill") return null;
    return typeof FillRenderer === "function" ? (
      <FillRenderer {...params} />
    ) : null;
  };
}

/**
 * Post-processes Order leg column definitions to support flattened fills.
 * Block-only columns get wrapped to return null for fill rows.
 * Dual columns (summary on block, individual on fill) get wrapped with both renderers.
 */
export function wrapColDefsForOrderFills(
  colDefs: ColDef[],
  dualColumnMap: Map<
    string,
    {
      fillRenderer: any;
      fillFormatter?: any;
      fillEditable?: (params: any) => boolean;
      fillEditor?: any;
      fillCellClass?: string;
      fillValueGetter?: (params: any) => any;
    }
  >,
  fillOnlyColumns: ColDef[],
  fullTradeData?: any,
): ColDef[] {
  const wrapped = colDefs.map((col) => {
    const colId = (col.colId || col.field) as string;

    // Dual columns: different renderer for block vs fill
    const dualConfig = dualColumnMap.get(colId);
    if (dualConfig) {
      // Ensure cellRendererParams has fullTradeData for fill renderers
      // Some block-level columns (e.g., filledAt) may not have cellRendererParams,
      // but the fill renderer needs fullTradeData to function correctly.
      const ensuredParams = col.cellRendererParams
        ? col.cellRendererParams
        : { fullTradeData };

      const wrappedCol: any = {
        ...col,
        cellRendererParams: ensuredParams,
        cellRenderer: wrapDualRenderer(
          col.cellRenderer,
          dualConfig.fillRenderer,
        ),
        valueFormatter:
          col.valueFormatter || dualConfig.fillFormatter
            ? wrapDualFormatter(col.valueFormatter, dualConfig.fillFormatter)
            : undefined,
      };

      // Add editability for fill rows (e.g., fill qty, fill price on RP)
      if (dualConfig.fillEditable) {
        const originalEditable = col.editable;
        wrappedCol.editable = (params: any) => {
          if (params.data?.rowType === "fill") {
            return dualConfig.fillEditable!(params);
          }
          // Block rows: delegate to original editable (if any)
          if (typeof originalEditable === "function")
            return originalEditable(params);
          return !!originalEditable;
        };
        wrappedCol.cellEditor = dualConfig.fillEditor;
        wrappedCol.cellEditorParams = { fullTradeData };
      }

      // Add conditional cellClass for fill rows
      if (dualConfig.fillCellClass) {
        const originalCellClass = col.cellClass;
        wrappedCol.cellClass = (params: any) => {
          if (params.data?.rowType === "fill") {
            return dualConfig.fillCellClass;
          }
          if (typeof originalCellClass === "function")
            return originalCellClass(params);
          return originalCellClass || "";
        };
      }

      // Add valueGetter so params.value resolves to the fill's own value
      // (not the block's summary value inherited from the spread)
      if (dualConfig.fillValueGetter) {
        const originalValueGetter = (col as any).valueGetter;
        wrappedCol.valueGetter = (params: any) => {
          if (params.data?.rowType === "fill") {
            return dualConfig.fillValueGetter!(params);
          }
          if (typeof originalValueGetter === "function")
            return originalValueGetter(params);
          // Default AG Grid behavior: read from data using the field
          return col.field ? params.data?.[col.field] : undefined;
        };
      }

      // Tooltips on dual columns should only show on block rows (summaries),
      // not on individual fill rows where they would be confusing
      if (wrappedCol.tooltipValueGetter) {
        const originalTooltip = wrappedCol.tooltipValueGetter;
        wrappedCol.tooltipValueGetter = (params: any) => {
          if (params.data?.rowType === "fill") return undefined;
          return originalTooltip(params);
        };
      }

      return wrappedCol;
    }

    // Block-only columns: return null/false/empty for fill rows
    if (blockOnlyColIds.includes(colId as ColId)) {
      const originalValueGetter = (col as any).valueGetter;
      const wrappedBlockCol: any = {
        ...col,
        cellRenderer: wrapBlockOnlyRenderer(col.cellRenderer),
        valueFormatter: wrapBlockOnlyFormatter(col.valueFormatter),
        // Return undefined for fill rows so copy/paste doesn't pick up
        // inherited block data (e.g., size, settlement date)
        valueGetter: (params: any) => {
          if (params.data?.rowType === "fill") return undefined;
          if (typeof originalValueGetter === "function")
            return originalValueGetter(params);
          return col.field ? params.data?.[col.field] : undefined;
        },
      };

      // Prevent editing on fill rows for block-only columns (e.g., settlement date on OP)
      if (col.editable) {
        const originalEditable = col.editable;
        wrappedBlockCol.editable = (params: any) => {
          if (params.data?.rowType === "fill") return false;
          if (typeof originalEditable === "function")
            return originalEditable(params);
          return !!originalEditable;
        };
      }

      // Remove cell class styling on fill rows
      if (col.cellClass) {
        const originalCellClass = col.cellClass;
        wrappedBlockCol.cellClass = (params: any) => {
          if (params.data?.rowType === "fill") return "";
          if (typeof originalCellClass === "function")
            return originalCellClass(params);
          return originalCellClass;
        };
      }

      // Remove tooltips on fill rows
      if ((col as any).tooltipValueGetter) {
        const originalTooltip = (col as any).tooltipValueGetter;
        wrappedBlockCol.tooltipValueGetter = (params: any) => {
          if (params.data?.rowType === "fill") return undefined;
          return originalTooltip(params);
        };
      }

      return wrappedBlockCol;
    }

    return col;
  });

  // Add fill-only columns at the end
  return [...wrapped, ...fillOnlyColumns];
}

interface IEditDealtStrike {
  structureType: StructureTypeCode;
  statusPassed: ExecutionRequestStatus | ExecutionStrategyStatus;
  securityType?: SecurityTypeCode;
}

export function canEditDealtStirke({
  structureType,
  statusPassed,
  securityType,
}: IEditDealtStrike): boolean {
  const allowedStructureTypes = [
    StructureTypeCode.BNDO,
    StructureTypeCode.OPTPACK,
    StructureTypeCode.SWO,
  ];

  if (
    allowedStructureTypes.includes(structureType) &&
    !defaultStatusCannotBeEdited.includes(statusPassed)
  ) {
    return true;
  }
  // 12366
  const allowedSecurityTypes = [SecurityTypeCode.BNDO, SecurityTypeCode.SWO];
  if (securityType) {
    if (
      allowedSecurityTypes.includes(securityType) &&
      statusPassed != ExecutionRequestStatus.Affirmed
    ) {
      return true;
    }
  }

  return false;
}

export function returnEridFromBlock(
  fullTradeData: ExecutionStrategy,
  blockIDpassed: number,
): string {
  if (!fullTradeData.executionRequestCounterParties) {
    return "0";
  }
  const erid = findCPOnTradeByBlockId(fullTradeData, blockIDpassed);
  return erid?.executionRequestId.toString() || "0";
}

// ---- QUOTE SIZE HELPERS ---- //
export function caluclatePercentageQuoteSizeER(params: any) {
  let allBlockNominalsSummed = 0;
  let totalQuotedSizeOfAllBlocks = 0;

  if (
    params &&
    params.data &&
    params.data.legs &&
    params.data.legs.length > 0
  ) {
    params.data.legs.forEach((leg: any) => {
      allBlockNominalsSummed = allBlockNominalsSummed + leg.notionalLeg1;
      totalQuotedSizeOfAllBlocks = totalQuotedSizeOfAllBlocks + leg.quotedSize;
    });
  }
  if (allBlockNominalsSummed > 0 || totalQuotedSizeOfAllBlocks > 0) {
    return (totalQuotedSizeOfAllBlocks / allBlockNominalsSummed) * 100;
  }
  return;
}

// export function quoteSizeInputValidationOnOutrightsRP(params: any): boolean {
//   let isOutright = params.data.strategyType == StrategyType.Outright;

//   if (!isOutright) {
//     return false;
//   }

//   const min = 1;
//   const max = isOutright
//     ? params.data.legs[0].notionalLeg1
//     : params.data.notionalLeg1;
//   // Ensure the new value is a number within the allowed range
//   if (params.newValue <= 0) {
//     toast.error("Quoted size cannot be 0 or less.");
//   } else if (params.newValue > max) {
//     toast.error("Quoted size cannot be larger than nominal.");
//   }

//   const newValue = Math.max(min, Math.min(max, Number(params.newValue)));
//   // If the value is unchanged, return false to prevent updates
//   if (params.data.legs[0][params.colDef.field] === newValue) {
//     return false;
//   }
//   // Update the value and return true to signal that the value was set
//   if (isOutright) {
//     params.data.legs[0][params.colDef.field] = newValue;
//     return true;
//   }
//   return false;
// }

// export function quoteSizeInputValidation(params: any) {
//   const min = 1;
//   const max = params.data.notionalLeg1; // Replace with your desired maximum value
//   // Ensure the new value is a number within the allowed range
//   const newValue = Math.max(min, Math.min(max, Number(params.newValue)));
//   if (params.newValue <= 0) {
//     toast.error("Quoted size cannot be 0 or less.");
//   } else if (params.newValue > max) {
//     toast.error("Quoted size cannot be larger than nominal.");
//   } else {
//     // updateQuotesAcrossTables(params);
//     return newValue;
//   }
// }

// UPDATE and New functionality:
// columnsToShowForNPVorRecoupon will be ADDED to the normal trade cols.
// columnsToHideForNPVorRecoupon will be REMOVED from the normal trade cols.
// returnColDefsAsNewArray to only run once and have NPV and Recoupon information included with the mapping.
export function returnColDefsAsNewArray(
  fullArrayOfColIds: any[],
  selectionOfColsToShow: ColId[],
  columnsToShowForNPVorRecoupon: ColId[], // SHOW
  columnsToHideForNPVorRecoupon: ColId[], // HIDE
  isOrder?: boolean,
) {
  const colDefs = fullArrayOfColIds.map((colDef: any) => {
    if (colDef && colDef.colId) {
      if (columnsToHideForNPVorRecoupon.includes(colDef.colId)) {
        // IF NPV says to hide = Priority - return colDef with hide: true instead of removing it
        return { ...colDef, hide: true };
      } else if (
        selectionOfColsToShow.includes(colDef.colId) || // Add based on normal rules.
        columnsToShowForNPVorRecoupon.includes(colDef.colId) // Secondary add based on NPV rules.
      ) {
        return { ...colDef, hide: false };
      } else {
        if (isOrder) {
          if (colDef.field !== "startFiller") {
            return colDef;
          }
        } else {
          return colDef;
        }
      }
    }
  });
  return colDefs.filter((col) => {
    if (col == undefined) {
    } else return col;
  });
}

// Uesd to return a block id at the RP level.
// This can change as outright needs to look at the leg level.
export function returnBlockId(thisTrade: ExecutionStrategy, params: any) {
  if (thisTrade.strategyType == StrategyType.Outright) {
    if (!params.data.legs || params.data.legs.length == 0) {
      return params.data.blockId; // check legs exist.
    }
    return params.data.legs[0].blockId;
  }
  return params.data.blockId;
}

// Used for Dealt Strike.
export function isStrikeEditable({
  params,
  esid,
  thisTradePassed,
}: {
  params: any;
  esid: string;
  thisTradePassed?: ExecutionStrategy;
}): boolean {
  function determineThisTrade() {
    const currentTrades = tradeDataStore.getState().tradeData;
    const thisTradeLive = currentTrades.find((trade) => trade.esid == esid);
    return thisTradeLive;
  }

  const thisTrade = thisTradePassed ? thisTradePassed : determineThisTrade();

  if (!thisTrade) {
    return false;
  }

  const { isOp, isOutright, isLeg, isER } =
    returnAllTradeDetailsFromGenericParams(params);

  const helperObject = {
    params,
    thisTrade,
    isOp,
    isER,
    isOutright,
    isLeg,
  };
  if (!helperObject) {
    return false;
  }

  const thisOPLeg = returnOPLegFormThisTrade(helperObject);

  if (isOp) {
    if (thisTrade && thisTrade.structureType && thisTrade.status) {
      return canEditDealtStirke({
        structureType: thisTrade.structureType,
        statusPassed: thisTrade.status,
        securityType: thisOPLeg?.securityType,
      });
    }
  }

  return false; // fallBack
}

export function isCPIEditable({
  esid,
  securityType,
  thisTradePassed,
}: {
  esid: string;
  securityType?: SecurityTypeCode; // Not needed on outrights.
  thisTradePassed?: ExecutionStrategy;
}): boolean {
  function determineThisTrade() {
    const currentTrades = tradeDataStore.getState().tradeData;
    const thisTradeLive = currentTrades.find((trade) => trade.esid == esid);
    return thisTradeLive;
  }
  const thisTrade = thisTradePassed ? thisTradePassed : determineThisTrade();
  if (!thisTrade) {
    return false;
  }
  // If sec code is passed in from a block, make sure it's a ZCS.
  if (securityType) {
    if (securityType != SecurityTypeCode.ZCS) {
      return false;
    }
  }

  // NEED TO REMOVE THIS ONCE THE RP CAN Edit
  if (thisTrade.role == "RP") {
    return false; // RP editing is causing issues with other RPs seeing the result.
  }

  if (thisTrade && thisTrade.status) {
    if (!defaultStatusCannotBeEdited.includes(thisTrade.status)) {
      return true;
    }
  }
  return false;
}

// LQC-227: Determines if a trade contains bond or bond-option legs.
// Used to conditionally show the ISIN column — for BND and BNDO trades.
// BNDO has instrumentType "OPT" but securityType "BNDO".
// Uses structureType for immediate detection (available on first render),
// then checks legs (OP: executionRequestCounterParties[0].legs, RP: .legs),
// then blocks fallback (used by the viewBlock/Legs table).
export function getStartFillerWidth(columnWidthMultiplier: number): number {
  if (columnWidthMultiplier === 1.2) return 59;
  if (columnWidthMultiplier === 0.9) return 50;
  return 53;
}

export function tradeHasBondOrBondOption(fullTradeData: any): boolean {
  if (!fullTradeData) return false;
  if (fullTradeData.structureType === StructureTypeCode.BNDO) return true;
  const isBondLeg = (leg: any) =>
    leg.instrumentType === InstrumentTypeCode.BND ||
    leg.securityType === SecurityTypeCode.BNDO;
  const opLegs =
    fullTradeData.executionRequestCounterParties?.[0]?.legs;
  if (opLegs?.some(isBondLeg)) return true;
  if (fullTradeData.legs?.some(isBondLeg)) return true;
  return fullTradeData.blocks?.some(isBondLeg) ?? false;
}
